import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import api from '@/lib/api';

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');

    if (!sessionId) {
      navigate('/auth', { replace: true });
      return;
    }

    const exchangeSession = async () => {
      try {
        const res = await api.get(`/auth/session?session_id=${sessionId}`);
        if (res.data?.user) {
          setUser(res.data.user);
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { replace: true });
        } else {
          setError('Sign-in failed. Please try again.');
        }
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (detail.includes('Invalid session') || detail.includes('Auth service')) {
          setError('Google sign-in session expired. Please try again.');
        } else {
          setError('Something went wrong connecting your Google account. Please try again.');
        }
      }
    };

    exchangeSession();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-6" data-testid="auth-callback-error">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h2 className="font-heading text-xl text-[#F2F2F2] mb-2">Sign-in failed</h2>
          <p className="text-[#A0A0A0] font-body text-sm mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => {
                window.history.replaceState(null, '', '/auth');
                navigate('/auth?mode=login', { replace: true });
              }}
              className="bg-[#D6B36A] text-[#0D0D0D] hover:bg-[#E8C881] font-body"
              data-testid="callback-try-again"
            >
              Try Again
            </Button>
            <button
              onClick={() => navigate('/auth?mode=signup', { replace: true })}
              className="text-[#A0A0A0] hover:text-[#F2F2F2] font-body text-sm transition-colors"
            >
              Or sign up with email instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center" data-testid="auth-callback">
      <p className="text-[#A0A0A0] font-heading text-xl animate-pulse">Connecting your account...</p>
    </div>
  );
}
